import React, { useState, useEffect } from "react";
import './Rogfilter.css'; // Use your existing filter CSS
import { getAvailabilityLabel } from "../utils/availability";
import { buildEmptyFilters } from "./filterUtils";

// Long option lists are collapsed to this many rows behind a "See more" toggle,
// which is what keeps the filter panel short enough to need no scrollbar.
const COLLAPSED_LIMIT = 5;

const FilterCheckboxGroup = ({
  title,
  groupKey,
  options,
  selected = [],
  expanded,
  onToggleExpand,
  onChange,
  renderLabel = (value) => value,
}) => {
  if (!options || options.length === 0) return null;

  // Collapsed: the first 5 in natural order, plus any checked option that falls
  // outside that window — otherwise an active filter's checkbox would disappear
  // while it is still filtering the grid. Appending rather than re-sorting keeps
  // rows from jumping around under the cursor when a box is ticked.
  const head = options.slice(0, COLLAPSED_LIMIT);
  const checkedBeyondHead = options
    .slice(COLLAPSED_LIMIT)
    .filter((opt) => selected.includes(opt));

  const visible = expanded ? options : [...head, ...checkedBeyondHead];
  const hiddenCount = options.length - visible.length;

  return (
    <div className="filter-section">
      <h3>{title}</h3>
      {visible.map((option) => (
        <label key={option}>
          <input
            type="checkbox"
            checked={selected.includes(option)}
            onChange={() => onChange(groupKey, option)}
          />
          {renderLabel(option)}
        </label>
      ))}
      {options.length > COLLAPSED_LIMIT && (
        <button
          type="button"
          className="see-more-btn"
          onClick={() => onToggleExpand(groupKey)}
        >
          {expanded ? 'See less' : `See more (${hiddenCount} more)`}
        </button>
      )}
    </div>
  );
};

const GenericFilter = ({
  products,
  priceRange,
  maxPrice = 0,
  filters = {},
  filterFields = [],
  onFilterChange,
}) => {
  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableConditions, setAvailableConditions] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Ensure filters have default values to prevent controlled/uncontrolled input issues
  const safeFilters = { ...buildEmptyFilters(filterFields), ...filters };

  // Extract unique values from products for filter options
  useEffect(() => {
    if (products && products.length > 0) {
      const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
      const models = [...new Set(products.map(p => p.model).filter(Boolean))].sort();
      const conditions = [...new Set(products.map(p => p.condition).filter(Boolean))].sort();

      setAvailableBrands(brands);
      setAvailableModels(models);
      setAvailableConditions(conditions);
    }
    // This component stays mounted across category switches, so collapse
    // everything again whenever the product set changes.
    setExpandedGroups({});
  }, [products]);

  const toggleExpandedGroup = (groupKey) => {
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handlePriceRangeChange = (e) => {
    onFilterChange(filters, Number(e.target.value));
  };

  const handleCheckboxChange = (category, value) => {
    const newFilters = { ...safeFilters };

    const current = newFilters[category] || [];
    newFilters[category] = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];

    onFilterChange(newFilters, priceRange);
  };

  const clearAllFilters = () => {
    onFilterChange(buildEmptyFilters(filterFields), maxPrice);
  };

  const hasActiveFilters = () => {
    return Object.values(safeFilters).some((v) => Array.isArray(v) && v.length > 0) ||
           (maxPrice > 0 && priceRange < maxPrice);
  };

  // Get unique availability statuses from actual products
  const getAvailableStatuses = () => {
    if (!products || products.length === 0) return [];

    const statuses = new Set();
    products.forEach(product => {
      statuses.add(getAvailabilityLabel(product));
    });

    return Array.from(statuses).sort();
  };

  // Get condition display name
  const getConditionDisplayName = (condition) => {
    switch(condition) {
      case 'brandnew': return 'Brand New';
      case 'used': return 'Used';
      default: return condition;
    }
  };

  const availableStatuses = getAvailableStatuses();

  // Only offer an admin-configured option if at least one loaded product carries it,
  // so a field defined before any product is tagged doesn't render an empty box.
  const optionsInUse = (field) => {
    const present = new Set(
      (products || []).map((p) => p.attributes?.[field.key]).filter(Boolean)
    );
    return (field.options || []).filter((opt) => present.has(opt));
  };

  const activeFields = filterFields
    .map((field) => ({ ...field, usableOptions: optionsInUse(field) }))
    .filter((field) => field.usableOptions.length > 0);

  return (
    <div className="rog-filter">
      {/* Clear Filters Button */}
      {hasActiveFilters() && (
        <button
          onClick={clearAllFilters}
          className="clear-all-filters-btn"
        >
          Clear All Filters
        </button>
      )}

      {/* Price Range — max comes from the highest priced product in this category */}
      {maxPrice > 0 && (
        <>
          <h2>PRICE RANGE</h2>
          <p className="price-range">0 RS - {Number(priceRange).toLocaleString()} RS</p>
          <input
            type="range"
            min="0"
            max={maxPrice}
            value={priceRange}
            onChange={handlePriceRangeChange}
            className="price-slider"
          />
        </>
      )}

      {/* Availability Filter */}
      <FilterCheckboxGroup
        title="AVAILABILITY"
        groupKey="availability"
        options={availableStatuses}
        selected={safeFilters.availability}
        expanded={expandedGroups.availability}
        onToggleExpand={toggleExpandedGroup}
        onChange={handleCheckboxChange}
      />

      {/* Condition Filter */}
      <FilterCheckboxGroup
        title="CONDITION"
        groupKey="condition"
        options={availableConditions}
        selected={safeFilters.condition}
        expanded={expandedGroups.condition}
        onToggleExpand={toggleExpandedGroup}
        onChange={handleCheckboxChange}
        renderLabel={getConditionDisplayName}
      />

      {/* Brand Filter - Only show if brands exist for this category */}
      <FilterCheckboxGroup
        title="BRAND"
        groupKey="brand"
        options={availableBrands}
        selected={safeFilters.brand}
        expanded={expandedGroups.brand}
        onToggleExpand={toggleExpandedGroup}
        onChange={handleCheckboxChange}
      />

      {/* Model Filter - Only show if models exist for this category */}
      <FilterCheckboxGroup
        title="MODEL"
        groupKey="model"
        options={availableModels}
        selected={safeFilters.model}
        expanded={expandedGroups.model}
        onToggleExpand={toggleExpandedGroup}
        onChange={handleCheckboxChange}
      />

      {/* Admin-configured filters for this category (e.g. Connectivity, Type) */}
      {activeFields.map((field) => (
        <FilterCheckboxGroup
          key={field.key}
          title={field.label.toUpperCase()}
          groupKey={field.key}
          options={field.usableOptions}
          selected={safeFilters[field.key]}
          expanded={expandedGroups[field.key]}
          onToggleExpand={toggleExpandedGroup}
          onChange={handleCheckboxChange}
        />
      ))}

      {/* Active Filters Count */}
      {hasActiveFilters() && (
        <div className="active-filters-display">
          <strong>Active Filters:</strong>
          <div className="active-filters-list">
            {safeFilters.availability.length > 0 && (
              <div>• Availability: {safeFilters.availability.length}</div>
            )}
            {safeFilters.condition.length > 0 && (
              <div>• Condition: {safeFilters.condition.length}</div>
            )}
            {safeFilters.brand.length > 0 && (
              <div>• Brands: {safeFilters.brand.length}</div>
            )}
            {safeFilters.model.length > 0 && (
              <div>• Models: {safeFilters.model.length}</div>
            )}
            {filterFields.map((field) =>
              (safeFilters[field.key] || []).length > 0 ? (
                <div key={field.key}>• {field.label}: {safeFilters[field.key].length}</div>
              ) : null
            )}
            {maxPrice > 0 && priceRange < maxPrice && (
              <div>• Price: Up to {Number(priceRange).toLocaleString()} RS</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericFilter;
